import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Cookies from 'js-cookie';
import { ApiError, LandingPage, RiderService } from 'services/openapi';
import useApi from 'services/mavapi/useApi';
import { useNavigate } from 'react-router-dom';
import isApiError from 'utils/isApiError';
import { useErrorHandlingSlice } from 'app/error/slice';
import { useDispatch } from 'react-redux';
import useRegistrationPageImageApi from 'admin/hooks/useRegistrationPageImageApi ';
import { formatImageUrl } from 'app/pages/LoginPage';
import CircularLoader from 'app/components/CircularLoader';

const bgImage =
  'https://images.unsplash.com/photo-1466495227171-d05d7e3ac2b3?q=80&w=1229&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

type Props = {};

const Dashboard = (props: Props) => {
  const currentUserId = Cookies.get('userId');
  const { riderGetLandingPage } = RiderService;
  const { error: apiError, handleRequest } = useApi();
  const {
    loading: isImageLoading,
    getRegistrationPageImageByType,
    imageByType,
  } = useRegistrationPageImageApi();
  const [, setError] = React.useState<ApiError | unknown>();
  const [riderDetails, setRiderDetails] = React.useState<LandingPage>();
  const navigate = useNavigate();
  const [enrolledDate, setEnrolledDate] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();

  function getRiderDetails(id?: string) {
    handleRequest(riderGetLandingPage(id))
      .then(res => {
        if (res && !isApiError(res)) {
          Cookies.set('sifID', res?.id);
          if (res.hasSIFForSeason) {
            setEnrolledDate(true);
            Cookies.set('hasSIFForSeason', res.hasSIFForSeason);
          }
        } else {
          dispatch(
            actions.dispatchToast({
              errorTxt: res.description,
              show: true,
              errorType: 'error',
            }),
          );
        }
        setRiderDetails(res);
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  }

  const handleJoinSifClick = () => {
    navigate('/rider-landing-page/sif');
  };

  React.useEffect(() => {
    getRiderDetails(currentUserId);
    getRegistrationPageImageByType(12);
  }, []);

  return (
    <HeroWrapper>
      {isImageLoading && (
        <LoaderOverlay>
          <CircularLoader />
        </LoaderOverlay>
      )}
      {!isImageLoading && (
        <HeroBg
          src={
            imageByType?.imageUrl
              ? formatImageUrl(imageByType?.imageUrl)
              : bgImage
          }
          alt=""
        />
      )}
      <HeroOverlay />

      <TitleBlock>
        <p className="welcome">Welcome</p>
        <h1>{Cookies.get('userName') || 'Rider'}</h1>
      </TitleBlock>

      <InfoCards>
        <InfoCard>
          <InfoCardLabel>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>Back Number</span>
          </InfoCardLabel>
          {loading ? (
            <SkeletonLine $width="50%" $height="28px" />
          ) : (
            <InfoCardNumber>{riderDetails?.backNumber || '—'}</InfoCardNumber>
          )}
        </InfoCard>

        <InfoCard>
          <InfoCardLabel>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Teams</span>
          </InfoCardLabel>
          {loading ? (
            <SkeletonLine $width="80%" />
          ) : (
            <InfoCardValue>{riderDetails?.team?.name || '—'}</InfoCardValue>
          )}
        </InfoCard>

        <InfoCard>
          <InfoCardLabel>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span>Coach</span>
          </InfoCardLabel>
          {loading ? (
            <>
              <SkeletonLine $width="70%" />
              <SkeletonLine $width="90%" $height="12px" />
              <SkeletonLine $width="60%" $height="12px" />
            </>
          ) : (
            <>
              <InfoCardValue>{riderDetails?.coachName || '—'}</InfoCardValue>
              {riderDetails?.coachEmail && (
                <InfoCardSub>{riderDetails.coachEmail}</InfoCardSub>
              )}
              {riderDetails?.coachPrimaryPhone && (
                <InfoCardSub>{riderDetails.coachPrimaryPhone}</InfoCardSub>
              )}
            </>
          )}
        </InfoCard>
      </InfoCards>

      <SifBanner>
        <SifBannerContent>
          <h2>Scholarship Incentive Fund</h2>
          <p>
            The Scholarship Incentive Fund (SIF) is designed to provide
            financial support and incentives for participants. Join the program
            today to unlock exclusive benefits and contribute to the future of
            equestrian sports.
          </p>
          {loading ? (
            <SkeletonLine $width="130px" $height="34px" $radius="10px" />
          ) : (
            !enrolledDate && (
              <JoinButton onClick={handleJoinSifClick}>
                Join SIF Today
              </JoinButton>
            )
          )}
        </SifBannerContent>
      </SifBanner>
    </HeroWrapper>
  );
};

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 0px);
  overflow: hidden;
  background: #1a1a1a;
`;

const HeroBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.35) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.2) 100%
  );
`;

const TitleBlock = styled.div`
  position: absolute;
  left: 90px;
  top: 120px;

  .welcome {
    font-size: 22px;
    color: rgba(255, 255, 255, 0.85);
    letter-spacing: 2px;
    margin: 0 0 8px;
    font-weight: 400;
    text-transform: uppercase;
  }

  h1 {
    font-family: 'Bebas Neue', 'Impact', sans-serif;
    font-size: 80px;
    font-weight: 400;
    color: #ffffff;
    line-height: 1;
    margin: 0;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
`;

const InfoCards = styled.div`
  position: absolute;
  left: 90px;
  bottom: 224px;
  display: flex;
  gap: 20px;
`;

const InfoCard = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border-radius: 14px;
  width: 199px;
  min-height: 160px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  backdrop-filter: blur(4px);
`;

const InfoCardLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;

  svg {
    flex-shrink: 0;
  }
`;

const InfoCardValue = styled.p`
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  margin: 4px 0 0;
  line-height: 1.4;
`;

const InfoCardNumber = styled.p`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0;
  text-align: center;
`;

const InfoCardSub = styled.p`
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  margin: 0;
  opacity: 0.9;
`;

const SifBanner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 204px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
`;

const SifBannerContent = styled.div`
  padding: 0 40px;
  width: 100%;

  h2 {
    color: #ffffff;
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 10px;
  }

  p {
    color: #ffffff;
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 16px;
    max-width: 900px;
    line-height: 1.6;
    opacity: 0.95;
  }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: calc(400px + 100%) 0; }
`;

const SkeletonLine = styled.span<{
  $width?: string;
  $height?: string;
  $radius?: string;
}>`
  display: block;
  width: ${({ $width }) => $width || '80%'};
  height: ${({ $height }) => $height || '16px'};
  border-radius: ${({ $radius }) => $radius || '4px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.08) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.08) 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`;

const JoinButton = styled.button`
  background: #0244fa;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #0235c8;
  }
`;

export default Dashboard;
